import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, NavLink } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import Dropdown from 'react-bootstrap/Dropdown';
import Alert from 'react-bootstrap/Alert';
import Spinner from 'react-bootstrap/Spinner';
import Landing from './pages/LandingDaylight';
import Upload from './pages/Upload-new';
import Dashboard from './pages/Dashboard-new';
import Profile from './pages/Profile-new';
import Login from './pages/Login-new';
import MfaChallenge from './pages/MfaChallenge-new';
import Register from './pages/Register-new';
import EarlyAccess from './pages/EarlyAccess-new';
import EarlyAccessApplicantsPage from './pages/EarlyAccessApplicants-new';
import SuperAdminPlatformSettings from './pages/SuperAdminPlatformSettings-new';
import SuperAdminSubscriptionPlans from './pages/SuperAdminSubscriptionPlans-new';
import SuperAdminOrgBilling from './pages/SuperAdminOrgBilling-new';
import SuperAdminPaymentHistory from './pages/SuperAdminPaymentHistory-new';
import SuperAdminLLMModels from './pages/SuperAdminLLMModels-new';
import SuperAdminFeedbackPage from './pages/SuperAdminFeedback-new';
import SuperAdminSupportTickets from './pages/SuperAdminSupportTickets-new';
import OrganizationSupportTickets from './pages/OrganizationSupportTickets-new';
import ForgotPassword from './pages/ForgotPassword-new';
import CheckInbox from './pages/CheckInbox-new';
import VerifyEmail from './pages/VerifyEmail-new';
import SetPassword from './pages/SetPassword-new';
import ProfileSetup from './pages/ProfileSetup-new';
import PrivacyPolicyPage from './pages/PrivacyPolicy-new';
import TermsOfServicePage from './pages/TermsOfService-new';
import ContactUsPage from './pages/ContactUs-new';
import FeedbackPage from './pages/Feedback-new';
import SuggestionsPage from './pages/Suggestions-new';
import ProtectedRoute from './components/ProtectedRoute';
import MaintenancePage from './components/MaintenancePage';
import Sidebar from './components/Sidebar-new';
import SiteFooter, { SiteNotice } from './components/SiteFooter';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import 'bootstrap/dist/css/bootstrap.min.css';
import './components/Sidebar-new.css';
import './components/SiteFooter.css';
import { PersonFill, BoxArrowRight, ChevronDown, BuildingFill, List as MenuIcon } from 'react-bootstrap-icons';
import { NotificationProvider } from './contexts/NotificationContext';
import NotificationBell from './components/NotificationBell';
import NotificationToasts from './components/NotificationToasts';
import AdminRoute from './components/AdminRoute';
import SuperAdminRoute from './components/SuperAdminRoute';
import SuperAdminAnalytics from './pages/SuperAdminAnalytics-new';
import OrganizationAnalytics from './pages/OrganizationAnalytics';
import UserManagement from './pages/UserManagement-new';
import AdminConfig from './pages/AdminConfig-new';
import OrganizationManagement from './pages/OrganizationManagement-new';
import OrganizationDetail from './pages/OrganizationDetail-new';
import RepositoryView from './pages/RepositoryView-new';
import RepositoryResults from './pages/RepositoryResults-new';
import ProjectsPage from './pages/Projects-new';
import ProjectCreatePage from './pages/ProjectCreate-new';
import ProjectDetailPage from './pages/ProjectDetail-new';
import SubscriptionBillingPage from './pages/SubscriptionBilling';
import PaymentHistoryPage from './pages/PaymentHistory-new';
import BillingUpgradePage from './pages/BillingUpgrade-new';
import TestExecutions from './pages/TestExecutions';
import TestExecutionDetail from './pages/TestExecutionDetail';
import TestDataPage from './pages/TestData';
import DataValidationPage from './pages/DataValidationPage';
import DataValidationFrameHost from './components/DataValidationFrameHost';
import AutomatedTestsPage from './pages/AutomatedTests';
import ExploratoryAgent from './pages/ExploratoryAgent';
import RitzReservationDemo from './pages/RitzReservationDemo';
import ServiceMaintenancePage from './pages/ServiceMaintenancePage-new';
import { ProjectProvider } from './contexts/ProjectContext';
import { GuidedTourProvider } from './tour/GuidedTourContext';
import { useActiveFeatureFlags, type ServiceMaintenanceKey } from './hooks/useActiveFeatureFlags';
import { SYNTHETIC_DATA_DISABLED_MESSAGE } from './constants/featureMessages';
import CountdownAcceliqa from './pages/CountdownAcceliqa';
import { isReleaseGateActive } from './utils/releaseGate';
import './responsive.css';

type GuardedFeature = 'test_execution' | 'synthetic_data' | 'data_validation_agent' | 'exploratory_agent' | 'ritz_reservation_demo';

const FEATURE_MAINTENANCE_SERVICE: Record<GuardedFeature, ServiceMaintenanceKey> = {
    test_execution: 'playwright_execution',
    synthetic_data: 'synthetic_data',
    data_validation_agent: 'data_validation',
    exploratory_agent: 'exploratory_agent',
    ritz_reservation_demo: 'ritz_reservation_demo',
};

function FeatureAccessGuard({ feature, children }: { feature: GuardedFeature; children: React.ReactElement }): React.ReactElement {
    const { flags, maintenance, loading } = useActiveFeatureFlags();
    const { user } = useAuth();
    const isSuperAdmin = Boolean(user && /superadmin/i.test(user.role));

    if (loading) {
        return (
            <Container
                fluid
                className="d-flex justify-content-center align-items-center gap-2"
                style={{ minHeight: 240, paddingTop: 24 }}
            >
                <Spinner animation="border" size="sm" role="status" aria-label="Loading" />
                <span className="text-muted">Loading…</span>
            </Container>
        );
    }

    const maintenanceStatus = maintenance[FEATURE_MAINTENANCE_SERVICE[feature]];
    if (!isSuperAdmin && flags[feature] && maintenanceStatus?.maintenance_enabled) {
        return (
            <MaintenancePage
                serviceName={maintenanceStatus.service_name}
                message={maintenanceStatus.message}
            />
        );
    }

    if (!flags[feature]) {
        if (feature === 'synthetic_data') {
            return (
                <Container fluid style={{ maxWidth: 900, paddingTop: 24 }}>
                    <Alert variant="warning" className="mb-0">
                        {SYNTHETIC_DATA_DISABLED_MESSAGE}
                    </Alert>
                </Container>
            );
        }

        const label =
            feature === 'test_execution'
                ? 'Test execution'
                : feature === 'data_validation_agent'
                  ? 'Data Validation'
                  : feature === 'ritz_reservation_demo'
                    ? 'Ritz Reservation Demo'
                    : 'Exploratory Agent';
        return (
            <Container fluid style={{ maxWidth: 900, paddingTop: 24 }}>
                <Alert variant="warning" className="mb-0">
                    {label} is not enabled for your organization. Contact your superadmin to enable this feature.
                </Alert>
            </Container>
        );
    }

    return children;
}

function AdminAnalyticsPage(): React.ReactElement
{
    const { user } = useAuth();
    const isSuperAdmin = Boolean(user && /superadmin/i.test(user.role));

    return isSuperAdmin ? <SuperAdminAnalytics /> : <OrganizationAnalytics />;
}

/* Redirects super admins away from the org-admin billing page,
   since super admins do not subscribe to plans themselves. */
function BillingRouteGuard({ children }: { children: React.ReactElement }): React.ReactElement
{
    const { user } = useAuth();
    const { maintenance, loading } = useActiveFeatureFlags();
    const isSuperAdmin = Boolean(user && /superadmin/i.test(user.role));
    if (isSuperAdmin)
    {
        return <Navigate to="/admin/org-billing" replace />;
    }
    if (loading)
    {
        return (
            <Container
                fluid
                className="d-flex justify-content-center align-items-center gap-2"
                style={{ minHeight: 240, paddingTop: 24 }}
            >
                <Spinner animation="border" size="sm" role="status" aria-label="Loading" />
                <span className="text-muted">Loading...</span>
            </Container>
        );
    }
    const billingMaintenance = maintenance.billing;
    if (billingMaintenance?.maintenance_enabled)
    {
        return (
            <MaintenancePage
                serviceName={billingMaintenance.service_name}
                message={billingMaintenance.message}
            />
        );
    }
    return children;
}

/* ============================================================
   AppTopbar — notification bell + user info + avatar dropdown.
   Shown on all authenticated pages.
   ============================================================ */
interface AppTopbarProps
{
    showNavigationToggle?: boolean;
    navigationOpen?: boolean;
    onOpenNavigation?: () => void;
}

function AppTopbar({ showNavigationToggle = false, navigationOpen = false, onOpenNavigation }: AppTopbarProps): React.ReactElement | null
{
    const location = useLocation();
    const { user, isAuthenticated, logout } = useAuth();

    /* Hide on public / onboarding pages */
    const publicPaths = ['/', '/login', '/mfa', '/register', '/early-access', '/forgot-password', '/check-inbox', '/auth/verify-email', '/privacy', '/terms', '/contact', '/suggestions', '/countdown', '/coming_soon_acceliqa'];
    /* Also hide on the set-password page (user is authenticated but should not see nav) */
    const noNavPaths = ['/set-password', '/profile-setup'];
    if (publicPaths.includes(location.pathname) || noNavPaths.includes(location.pathname) || !isAuthenticated)
    {
        return null;
    }

    /* Logout handler */
    function handleLogout(): void
    {
        logout();
        window.location.href = '/login';
    }

    /* Build user initials from the full name */
    function getUserInitials(): string
    {
        if (!user?.name)
        {
            return '';
        }
        const parts = user.name.trim().split(/\s+/);
        if (parts.length >= 2)
        {
            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        }
        return parts[0].substring(0, 2).toUpperCase();
    }

    /* Capitalize role string for display */
    function getDisplayRole(): string
    {
        if (!user)
        {
            return '';
        }

        const normalizedRole = String(user.role || '').trim().toLowerCase();
        if (normalizedRole === 'superadmin')
        {
            return 'Super Admin';
        }

        const normalizedOrganizationRole = String(user.organization_role || '').trim().toLowerCase();
        if (normalizedOrganizationRole === 'admin')
        {
            return 'Admin';
        }

        if (normalizedRole === 'admin')
        {
            return 'Admin';
        }

        return 'User';
    }

    function getActiveOrganizationName(): string
    {
        const directName = String(user?.organization_name || '').trim();
        if (directName)
        {
            return directName;
        }

        const activeOrganizationId = Number(user?.active_organization_id || user?.organization_id || 0);
        if (activeOrganizationId && Array.isArray(user?.organizations))
        {
            const activeOrganization = user.organizations.find((organization) => organization.id === activeOrganizationId);
            const activeOrganizationName = String(activeOrganization?.name || '').trim();
            if (activeOrganizationName)
            {
                return activeOrganizationName;
            }
        }

        return 'No active organization';
    }

    const activeOrganizationName = getActiveOrganizationName();
    const hasActiveOrganization = activeOrganizationName !== 'No active organization';

    return (
        <div className="topbar">
            <div className="topbar-leading">
                {showNavigationToggle && (
                    <button
                        type="button"
                        className="topbar-navigation-toggle"
                        aria-label="Open navigation"
                        aria-controls="app-primary-navigation"
                        aria-expanded={navigationOpen}
                        onClick={onOpenNavigation}
                    >
                        <MenuIcon size={22} aria-hidden="true" />
                    </button>
                )}
                <div className="topbar-org-context" title={`Active organization: ${activeOrganizationName}`}>
                    <BuildingFill className="topbar-org-icon" />
                    <span className="topbar-org-text">
                        {hasActiveOrganization && <span className="topbar-org-prefix">Welcome to</span>}
                        <span className="topbar-org-name">{activeOrganizationName}</span>
                    </span>
                </div>
            </div>

            {/* Right section: bell + user info */}
            <div className="topbar-right">
                {/* Notification bell + dropdown */}
                <NotificationBell />

                {/* User info + avatar dropdown */}
                <Dropdown align="end">
                    <Dropdown.Toggle
                        variant="light"
                        id="topbar-profile-dropdown"
                        className="topbar-profile-toggle"
                    >
                        <div className="topbar-user-info">
                            <span className="topbar-user-name">{user?.name}</span>
                            <span className="topbar-user-role">{getDisplayRole()}</span>
                        </div>
                        <div className="topbar-avatar">
                            {getUserInitials()}
                        </div>
                        <ChevronDown size={10} className="topbar-chevron" />
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="topbar-profile-menu">
                        <div className="topbar-profile-menu-header">
                            <div className="topbar-profile-menu-name">{user?.name}</div>
                            <div className="topbar-profile-menu-email">{user?.email}</div>
                        </div>
                        <div className="topbar-profile-menu-body">
                            <NavLink to="/profile" className="topbar-profile-item topbar-profile-link">
                                <PersonFill className="topbar-profile-item-icon" />
                                <span>My Account</span>
                            </NavLink>
                            <div className="topbar-profile-divider" />
                            <button type="button" onClick={handleLogout} className="topbar-profile-item topbar-profile-link topbar-profile-item-danger">
                                <BoxArrowRight className="topbar-profile-item-icon" />
                                <span>Logout</span>
                            </button>
                        </div>
                    </Dropdown.Menu>
                </Dropdown>
            </div>
        </div>
    );
}

/* ============================================================
   AppRoutes — all route definitions.
   Public pages render full-screen, authenticated pages in container.
   ============================================================ */
function AppRoutes(): React.ReactElement
{
    const location = useLocation();

    // Public page paths that should render full-screen
    const publicPaths = ['/', '/login', '/mfa', '/register', '/early-access', '/forgot-password', '/check-inbox', '/auth/verify-email', '/privacy', '/terms', '/contact', '/suggestions', '/countdown', '/coming_soon_acceliqa'];
    // Authenticated paths that need no sidebar/container (full-screen locked flow)
    const noNavPaths = ['/set-password', '/profile-setup'];
    const isPublicPage = publicPaths.includes(location.pathname);
    const isNoNavPage = noNavPaths.includes(location.pathname);

    return (
        <>
            {isPublicPage ? (
                <Routes>
                    <Route path="/" element={<Landing />} />
                    <Route path="/login" element={<><Landing /><Login /></>} />
                    <Route path="/mfa" element={<><Landing /><MfaChallenge /></>} />
                    <Route path="/register" element={<><Landing /><Register /></>} />
                    <Route path="/early-access" element={<><Landing /><EarlyAccess /></>} />
                    <Route path="/forgot-password" element={<><Landing /><ForgotPassword /></>} />
                    <Route path="/check-inbox" element={<><Landing /><CheckInbox /></>} />
                    <Route path="/auth/verify-email" element={<><Landing /><VerifyEmail /></>} />
                    <Route path="/privacy" element={<PrivacyPolicyPage />} />
                    <Route path="/terms" element={<TermsOfServicePage />} />
                    <Route path="/contact" element={<ContactUsPage />} />
                    <Route path="/suggestions" element={<SuggestionsPage />} />
                    <Route path="/countdown" element={<CountdownAcceliqa />} />
                    <Route path="/coming_soon_acceliqa" element={<Navigate to="/countdown" replace />} />
                </Routes>
            ) : isNoNavPage ? (
                <Routes>
                    <Route path="/set-password" element={
                        <ProtectedRoute><SetPassword /></ProtectedRoute>
                    } />
                    <Route path="/profile-setup" element={
                        <ProtectedRoute><ProfileSetup /></ProtectedRoute>
                    } />
                </Routes>
            ) : (
                <Container fluid className="app-route-container">
                    <Routes>
                        <Route path="/upload" element={
                            <ProtectedRoute><Upload /></ProtectedRoute>
                        } />
                        <Route path="/dashboard" element={
                            <ProtectedRoute><Dashboard /></ProtectedRoute>
                        } />
                        <Route path="/exploratory_agent" element={
                            <ProtectedRoute><FeatureAccessGuard feature="exploratory_agent"><ExploratoryAgent /></FeatureAccessGuard></ProtectedRoute>
                        } />
                        <Route path="/ritz-reservation-demo" element={
                            <ProtectedRoute><FeatureAccessGuard feature="ritz_reservation_demo"><RitzReservationDemo /></FeatureAccessGuard></ProtectedRoute>
                        } />
                        <Route path="/admin/analytics" element={
                            <ProtectedRoute><AdminRoute><AdminAnalyticsPage /></AdminRoute></ProtectedRoute>
                        } />
                        <Route path="/projects" element={
                            <ProtectedRoute><ProjectsPage /></ProtectedRoute>
                        } />
                        <Route path="/data-validation" element={
                            <ProtectedRoute><FeatureAccessGuard feature="data_validation_agent"><DataValidationPage /></FeatureAccessGuard></ProtectedRoute>
                        } />
                        <Route path="/projects/new" element={
                            <ProtectedRoute><ProjectCreatePage /></ProtectedRoute>
                        } />
                        <Route path="/projects/:projectId" element={
                            <ProtectedRoute><ProjectDetailPage /></ProtectedRoute>
                        } />
                        <Route path="/projects/:projectId/repository" element={
                            <ProtectedRoute><RepositoryView /></ProtectedRoute>
                        } />
                        <Route path="/projects/:projectId/repository/results" element={
                            <ProtectedRoute><RepositoryResults /></ProtectedRoute>
                        } />
                        <Route path="/projects/:projectId/executions" element={
                            <ProtectedRoute><FeatureAccessGuard feature="test_execution"><TestExecutions /></FeatureAccessGuard></ProtectedRoute>
                        } />
                        <Route path="/projects/:projectId/executions/:jobId" element={
                            <ProtectedRoute><FeatureAccessGuard feature="test_execution"><TestExecutionDetail /></FeatureAccessGuard></ProtectedRoute>
                        } />
                        <Route path="/projects/:projectId/test-data" element={
                            <ProtectedRoute><FeatureAccessGuard feature="synthetic_data"><TestDataPage /></FeatureAccessGuard></ProtectedRoute>
                        } />
                        <Route path="/projects/:projectId/automated-tests" element={
                            <ProtectedRoute><FeatureAccessGuard feature="test_execution"><AutomatedTestsPage /></FeatureAccessGuard></ProtectedRoute>
                        } />
                        <Route path="/repositories" element={<Navigate to="/projects" replace />} />
                        <Route path="/test-generations" element={
                            <Navigate to="/projects" replace />
                        } />
                        <Route path="/profile" element={
                            <ProtectedRoute><Profile /></ProtectedRoute>
                        } />
                        <Route path="/feedback" element={
                            <ProtectedRoute><FeedbackPage /></ProtectedRoute>
                        } />
                        <Route path="/billing" element={
                            <ProtectedRoute><AdminRoute><BillingRouteGuard><SubscriptionBillingPage /></BillingRouteGuard></AdminRoute></ProtectedRoute>
                        } />
                        <Route path="/billing/upgrade" element={
                            <ProtectedRoute><AdminRoute><BillingRouteGuard><BillingUpgradePage /></BillingRouteGuard></AdminRoute></ProtectedRoute>
                        } />
                        <Route path="/billing/payment-history" element={
                            <ProtectedRoute><AdminRoute><BillingRouteGuard><PaymentHistoryPage /></BillingRouteGuard></AdminRoute></ProtectedRoute>
                        } />
                        <Route path="/support-tickets" element={
                            <ProtectedRoute><AdminRoute><BillingRouteGuard><OrganizationSupportTickets /></BillingRouteGuard></AdminRoute></ProtectedRoute>
                        } />
                        <Route path="/maintenance/:serviceKey" element={
                            <ProtectedRoute><ServiceMaintenancePage /></ProtectedRoute>
                        } />
                        <Route path="/subscription-billing" element={<Navigate to="/billing" replace />} />
                        <Route path="/admin/users" element={
                            <ProtectedRoute><AdminRoute><UserManagement /></AdminRoute></ProtectedRoute>
                        } />
                        <Route path="/admin/organizations" element={
                            <ProtectedRoute><AdminRoute><OrganizationManagement /></AdminRoute></ProtectedRoute>
                        } />
                        <Route path="/admin/organizations/:orgId" element={
                            <ProtectedRoute><AdminRoute><OrganizationDetail /></AdminRoute></ProtectedRoute>
                        } />
                        <Route path="/admin/config" element={
                            <ProtectedRoute><AdminRoute><AdminConfig /></AdminRoute></ProtectedRoute>
                        } />
                        <Route path="/admin/platform-settings" element={
                            <ProtectedRoute><SuperAdminRoute><SuperAdminPlatformSettings /></SuperAdminRoute></ProtectedRoute>
                        } />
                        <Route path="/admin/early-access" element={
                            <ProtectedRoute><SuperAdminRoute><EarlyAccessApplicantsPage /></SuperAdminRoute></ProtectedRoute>
                        } />
                        <Route path="/admin/feedback" element={
                            <ProtectedRoute><SuperAdminRoute><SuperAdminFeedbackPage /></SuperAdminRoute></ProtectedRoute>
                        } />
                        <Route path="/admin/support-tickets" element={
                            <ProtectedRoute><SuperAdminRoute><SuperAdminSupportTickets /></SuperAdminRoute></ProtectedRoute>
                        } />
                        <Route path="/admin/token-plans" element={
                            <Navigate to="/admin/subscription-plans" replace />
                        } />
                        <Route path="/admin/subscription-plans" element={
                            <ProtectedRoute><SuperAdminRoute><SuperAdminSubscriptionPlans /></SuperAdminRoute></ProtectedRoute>
                        } />
                        <Route path="/admin/org-billing" element={
                            <ProtectedRoute><SuperAdminRoute><SuperAdminOrgBilling /></SuperAdminRoute></ProtectedRoute>
                        } />
                        <Route path="/admin/payment-history" element={
                            <ProtectedRoute><SuperAdminRoute><SuperAdminPaymentHistory /></SuperAdminRoute></ProtectedRoute>
                        } />
                        <Route path="/admin/llm-models" element={
                            <ProtectedRoute><SuperAdminRoute><SuperAdminLLMModels /></SuperAdminRoute></ProtectedRoute>
                        } />
                        <Route path="*" element={<Navigate to="/" />} />
                    </Routes>
                </Container>
            )}
        </>
    );
}

/* ============================================================
   AppLayout — sidebar + topbar + main content area.
   Manages collapsed/expanded state for the sidebar.
   ============================================================ */
function AppLayout(): React.ReactElement
{
    /* Sidebar collapsed state — lifted here so the main content offset stays in sync */
    const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(true);
    const [mobileNavigationOpen, setMobileNavigationOpen] = useState<boolean>(false);
    const [isMobileViewport, setIsMobileViewport] = useState<boolean>(() => {
        return typeof window !== 'undefined' && window.matchMedia('(max-width: 767.98px)').matches;
    });
    const location = useLocation();
    const { isAuthenticated } = useAuth();

    useEffect(() => {
        const mediaQuery = window.matchMedia('(max-width: 767.98px)');
        const handleViewportChange = (event: MediaQueryListEvent): void => {
            setIsMobileViewport(event.matches);
            if (!event.matches)
            {
                setMobileNavigationOpen(false);
            }
        };

        setIsMobileViewport(mediaQuery.matches);
        mediaQuery.addEventListener('change', handleViewportChange);
        return () => mediaQuery.removeEventListener('change', handleViewportChange);
    }, []);

    useEffect(() => {
        setMobileNavigationOpen(false);
    }, [location.pathname, location.search]);

    useEffect(() => {
        if (!mobileNavigationOpen)
        {
            return undefined;
        }

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const handleKeyDown = (event: KeyboardEvent): void => {
            if (event.key === 'Escape')
            {
                setMobileNavigationOpen(false);
            }
        };
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [mobileNavigationOpen]);

    /* Toggle handler passed to Sidebar */
    function handleSidebarToggle(): void
    {
        if (isMobileViewport)
        {
            setMobileNavigationOpen(false);
            return;
        }
        setSidebarCollapsed((prev) => !prev);
    }

    /* Determine if we're on a public page (no sidebar) */
    const publicPaths = ['/', '/login', '/mfa', '/register', '/early-access', '/forgot-password', '/check-inbox', '/auth/verify-email', '/privacy', '/terms', '/contact', '/suggestions', '/countdown', '/coming_soon_acceliqa'];
    const noNavPaths = ['/set-password', '/profile-setup'];
    const isPublicPage = publicPaths.includes(location.pathname) || !isAuthenticated;
    const isNoNavPage = noNavPaths.includes(location.pathname);

    /* Content offset class based on sidebar state */
    const contentClass = (isPublicPage || isNoNavPage)
        ? ''
        : isMobileViewport
            ? 'sidebar-main-content sidebar-content-mobile'
        : sidebarCollapsed
            ? 'sidebar-main-content sidebar-content-collapsed'
            : 'sidebar-main-content sidebar-content-expanded';

    const shouldRenderSidebar = !isPublicPage && !isNoNavPage && (!isMobileViewport || mobileNavigationOpen);

    return (
        <>
            {/* Sidebar navigation: hidden on public and no-nav pages */}
            {isMobileViewport && mobileNavigationOpen && (
                <button
                    type="button"
                    className="sidebar-mobile-backdrop"
                    aria-label="Dismiss navigation"
                    onClick={() => setMobileNavigationOpen(false)}
                />
            )}

            {shouldRenderSidebar && (
                <Sidebar
                    collapsed={isMobileViewport ? false : sidebarCollapsed}
                    mobile={isMobileViewport}
                    onToggle={handleSidebarToggle}
                />
            )}

            {/* Main content area */}
            <div className={contentClass}>
                {/* Top bar with search + profile */}
                <AppTopbar
                    showNavigationToggle={!isPublicPage && !isNoNavPage && isMobileViewport}
                    navigationOpen={mobileNavigationOpen}
                    onOpenNavigation={() => setMobileNavigationOpen(true)}
                />

                {/* Shared product notice below the header */}
                {!isPublicPage && !isNoNavPage && <SiteNotice />}

                {/* Page routes */}
                <div className="app-routes-shell" style={{ position: 'relative' }}>
                    <DataValidationFrameHost />
                    <AppRoutes />
                </div>

                {/* Shared footer on authenticated and no-nav flows */}
                {!isPublicPage && !isNoNavPage && <SiteFooter />}
            </div>
        </>
    );
}

function ReleaseGateBoundary(): React.ReactElement
{
    const location = useLocation();
    const [, setNow] = useState<number>(() => Date.now());

    useEffect(() => {
        const timer = window.setInterval(() => {
            setNow(Date.now());
        }, 1000);

        return () => window.clearInterval(timer);
    }, []);

    const gateActive = isReleaseGateActive();
    const onCountdownPage = location.pathname === '/countdown';

    // While the release gate is active, everyone is sent to /countdown, which shows
    // the branded coming-soon page (CountdownAcceliqa). Once the gate opens, that
    // page redirects back home.
    if (gateActive && !onCountdownPage)
    {
        return <Navigate to="/countdown" replace />;
    }

    if (gateActive && onCountdownPage)
    {
        return <CountdownAcceliqa />;
    }

    if (!gateActive && onCountdownPage)
    {
        return <Navigate to="/" replace />;
    }

    return (
        <AuthProvider>
            <NotificationProvider>
                <ProjectProvider>
                    <GuidedTourProvider>
                        <AppLayout />
                        <NotificationToasts />
                    </GuidedTourProvider>
                </ProjectProvider>
            </NotificationProvider>
        </AuthProvider>
    );
}

/* ============================================================
   App — root component with providers and router.
   ============================================================ */
function App(): React.ReactElement
{
    return (
        <Router>
            <ReleaseGateBoundary />
        </Router>
    );
}

export default App;
